import React, { useState } from "react";
import { toast } from "sonner";
import { finApi, finErr, money } from "@/lib/financeApi";
import { btnGold, field } from "@/components/nds/windows/finance/parts";
import { SPLIT_KEYS } from "@/components/nds/windows/finance/PaycheckForm";
import { LoanFields, postLoan, useLoan } from "@/components/nds/windows/session/SessionLoan";

export function PaycheckEdit({ pc, onDone, onCancel, correct = false, anywhere = false }) {
  const [f, setF] = useState({ pay_period_start: pc.pay_period_start, pay_period_end: pc.pay_period_end, employer: pc.employer || "",
    gross_amount: pc.gross_amount, split: { ...pc.split }, reason: "" });
  const [busy, setBusy] = useState(false);
  const [loan, setLoan] = useState({ kind: "add", amount: "" });
  const lo = useLoan(pc.resident_id, anywhere);
  const showLoan = correct && lo.balance !== false;
  const loanAmt = showLoan ? Number(loan.amount) || 0 : 0;
  const loanBad = loanAmt < 0 || (loan.kind === "repay" && loanAmt > lo.balance);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const setSplit = (k) => (e) => setF((x) => ({ ...x, split: { ...x.split, [k]: e.target.value } }));
  const total = Object.values(f.split).reduce((a, v) => a + (Number(v) || 0), 0);
  const remaining = Math.round(((Number(f.gross_amount) || 0) - total) * 100) / 100;
  const legacyLeft = correct && pc.legacy_import ? Math.round((pc.gross_amount - Object.values(pc.split).reduce((x, v) => x + (Number(v) || 0), 0)) * 100) / 100 : 0;
  const balanced = remaining === 0 || (legacyLeft !== 0 && remaining === legacyLeft);
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = { ...f, gross_amount: Number(f.gross_amount), split: Object.fromEntries(Object.entries(f.split).map(([k, v]) => [k, Number(v) || 0])) };
      if (!correct) await finApi.patch(`/paychecks/${pc.id}`, body);
      else if (changed) await finApi.post(`/paychecks/${pc.id}/correct-split`, body);
      if (loanAmt > 0) { await postLoan(pc.resident_id, lo.checkinId, loan, f.reason.trim(), pc.id); lo.reload(); }
      toast.success(!correct ? "Paycheck updated · change recorded" : [changed && "Ledger correction posted", loanAmt > 0 && (loan.kind === "add" ? "added to line of credit" : "line of credit repayment recorded")].filter(Boolean).join(" · ").replace(/^./, (c) => c.toUpperCase()));
      onDone();
    } catch (err) { toast.error(finErr(err)); } finally { setBusy(false); }
  };
  const id = pc.id;
  const changed = ["pay_period_start", "pay_period_end", "employer"].some((k) => (f[k] || "") !== (pc[k] || "")) || Number(f.gross_amount) !== pc.gross_amount
    || SPLIT_KEYS.some(([k]) => (Number(f.split[k]) || 0) !== (pc.split[k] || 0));
  const diffs = correct ? SPLIT_KEYS.filter(([k]) => k !== "tithe").map(([k, l]) => [l, Math.round(((Number(f.split[k]) || 0) - (pc.split[k] || 0)) * 100) / 100]).filter(([, d]) => d) : [];
  return (
    <form onSubmit={save} data-testid={`fin-paycheck-${correct ? "correction" : "edit"}-form-${id}`} className={`p-3 border rounded-sm space-y-2 ${correct ? "border-red-400/30 bg-red-400/[0.03]" : "border-nex-gold/30 bg-white/[0.02]"}`}>
      {correct && <div className="text-[11px] text-nex-muted">Ledger correction · change the numbers below. Only the differences post to the ledger, recorded in the money trail. Super admin only.</div>}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <input type="date" value={f.pay_period_start} onChange={set("pay_period_start")} data-testid={`fin-paycheck-edit-start-${id}`} className={field} />
        <input type="date" value={f.pay_period_end} onChange={set("pay_period_end")} data-testid={`fin-paycheck-edit-end-${id}`} className={field} />
        <input value={f.employer} onChange={set("employer")} placeholder="Employer" data-testid={`fin-paycheck-edit-employer-${id}`} className={field} />
        <input type="number" min="0.01" step="0.01" value={f.gross_amount} onChange={set("gross_amount")} data-testid={`fin-paycheck-edit-gross-${id}`} className={field} />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        {SPLIT_KEYS.map(([k, label]) => (
          <label key={k} className="space-y-1"><span className="nds-label-dim text-[9px] block truncate" title={label}>{label === "PACT Fee" ? "Case mgmt" : label}</span>
            <input type="number" min="0" step="0.01" value={f.split[k]} onChange={setSplit(k)} data-testid={`fin-paycheck-edit-split-${k}-${id}`} className={`${field} w-full`} /></label>
        ))}
      </div>
      {correct && <div data-testid={`fin-correction-diff-${id}`} className="font-mono text-[10px] text-nex-silver">{diffs.length ? `Will post: ${diffs.map(([l, d]) => `${l} ${d > 0 ? "+" : "−"}${money(Math.abs(d))}`).join(" · ")}` : "No bucket changes yet"}</div>}
      {showLoan && <LoanFields id={id} balance={lo.balance} loan={loan} setLoan={setLoan} />}
      <input value={f.reason} onChange={set("reason")} placeholder={showLoan ? "Reason / line of credit note (required)" : "Reason for the change (required)"} data-testid={`fin-paycheck-edit-reason-${id}`} className={`${field} w-full`} />
      <div className="flex items-center gap-2">
        <span data-testid={`fin-paycheck-edit-remaining-${id}`} className={`font-mono text-[11px] ${balanced ? "text-emerald-300" : "text-yellow-300"}`}>
          {remaining === 0 ? "Split balanced" : balanced ? `${money(remaining)} unsplit on the old platform · left as is` : `${money(remaining)} left to split`}
        </span>
        <button type="button" onClick={onCancel} data-testid={`fin-paycheck-edit-cancel-${id}`} className="ml-auto font-mono text-[10px] uppercase text-nex-muted">Cancel</button>
        <button type="submit" disabled={busy || !balanced || !f.reason.trim() || (correct && (loanBad || (!changed && !loanAmt)))} data-testid={`fin-paycheck-edit-save-${id}`} className={btnGold}>{correct ? "Post correction" : "Save changes"}</button>
      </div>
    </form>
  );
}

const show = (v) => (v && typeof v === "object" ? Object.entries(v).map(([k, x]) => `${k} ${x}`).join(", ") : String(v ?? "—"));

export function PaycheckHistory({ pc }) {
  const [open, setOpen] = useState(false);
  if (!(pc.history || []).length) return null;
  return (
    <div>
      <button type="button" onClick={() => setOpen((o) => !o)} data-testid={`fin-paycheck-history-toggle-${pc.id}`} className="font-mono text-[10px] uppercase text-nex-muted hover:text-nex-silver">
        {open ? "Hide" : "Show"} money trail · {pc.history.length}
      </button>
      {open && (
        <ul data-testid={`fin-paycheck-history-${pc.id}`} className="mt-1 space-y-1 text-[11px] text-nex-muted">
          {pc.history.map((h, i) => (
            <li key={i}>
              {new Date(h.at).toLocaleString()} · {h.actor?.name || "—"} · {h.from_status ? `${h.from_status} → ` : ""}{h.to_status}{h.note ? ` · ${h.note}` : ""}
              {h.changes && Object.entries(h.changes).map(([k, c]) => <div key={k} className="pl-3">{k}: {show(c.from)} → {show(c.to)}</div>)}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
